import { useEffect, useState, useCallback, useRef } from "react";
import { getPaginatedUserGroupList } from "../services/groupService";

interface IUseFetchGroups {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  page: number;
  pSize: number;
  searchTerm?: string;
  userGroupFilterIds?: string;
  isActive?: string;
}

const usePaginatedFetchGroupsList = ({
  setIsLoading,
  page,
  pSize,
  searchTerm,
  userGroupFilterIds,
  isActive = "true",
}: IUseFetchGroups) => {
  const [groupsData, setGroupsData] = useState<any>({});
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(
    async (searchQuery?: string) => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      try {
        const result = await getPaginatedUserGroupList({
          page,
          pSize,
          searchTerm: searchQuery || searchTerm,
          userGroupFilterIds,
          isActive,
          signal: abortControllerRef.current.signal,
        });
        setGroupsData(result);
      } catch (err: any) {
        // Don't update state if request was aborted
        if (err.name !== "AbortError") {
          setGroupsData({});
        }
      } finally {
        setIsLoading(false);
      }
    },
    [page, pSize, searchTerm, userGroupFilterIds, isActive, setIsLoading]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup function to abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return [groupsData, fetchData];
};

export default usePaginatedFetchGroupsList;
